import {Injectable} from '@angular/core';
import moment from 'moment';
import {defer, merge, NEVER, Observable, of, OperatorFunction, pipe, Subject} from 'rxjs';
import {filter, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import {SecurityService} from '../../../../components/security';
import {isSmartSecurityKey, SmartSecurity, SmartSecurityService} from '../../../../components/smart-security';
import {WebSocketMessage} from '../../../../components/streaming/streaming-interfaces';
import {
    SubscriptionStreamingService,
    SubscriptionSubject
} from '../../../../components/streaming/subscription-streaming.service';
import {SubscriptionSubjectType} from '../../../../components/streaming/subscription-subject-type';
import {ResolutionString} from '../../../../components/trading-view-chart/charting-library/datafeed-api';
import {Candle} from '../../../../models/candle';
import {formatSecurityKey, parseSecurityKey, SecurityKey} from '../../../../models/security-key';
import {CHART_RESOLUTIONS_DICT} from '../chart-resolution';

enum CandlesWebSocketMessageType {
    CANDLES_SNAPSHOT = 'CANDLES_SNAPSHOT',
    CANDLES_UPDATE   = 'CANDLES_UPDATE'
}

interface CandlesWsMessage extends WebSocketMessage {
    messageType: CandlesWebSocketMessageType;
    data: Candle[] | CandlesSnapshotWsMessageData;
}

interface CandlesSnapshotWsMessage extends CandlesWsMessage {
    messageType: CandlesWebSocketMessageType.CANDLES_SNAPSHOT;
    data: CandlesSnapshotWsMessageData;
}

interface CandlesUpdateWsMessage extends CandlesWsMessage {
    messageType: CandlesWebSocketMessageType.CANDLES_UPDATE;
    data: Candle[];
}

interface CandlesSnapshotWsMessageData {
    requestId: number | null;
    data: Candle[];
}

const CANDLES_LIMIT: number = 10;

@Injectable()
export class CandleStreamingService {

    private static createDataBankId(securityKeyStr: string, candlePeriod: string): string {
        return `${securityKeyStr}:${candlePeriod}`;
    }

    private static createDataBankIdByCandle(candle: Candle): string {
        const securityKeyStr = formatSecurityKey(candle.security);

        return CandleStreamingService.createDataBankId(securityKeyStr, candle.candlePeriod);
    }

    private static createDataBankIdBySecurityKeyAndResolution(securityKeyStr: string, resolution: ResolutionString): string {
        const candlePeriod = CHART_RESOLUTIONS_DICT[resolution].candlePeriod;

        return CandleStreamingService.createDataBankId(securityKeyStr, candlePeriod);
    }

    public readonly subscriptionSent$: Observable<any>;
    public readonly disconnected$: Observable<any>;

    private readonly subscriptionSentSubject = new Subject<any>();
    private readonly disconnectedSubject = new Subject<any>();

    private subscriptionSubjectType: SubscriptionSubjectType<Candle> = {
        name: 'CANDLES',
        isMultiSubject: true,
        splitSubjectOnSubscribe: true,
        operations: {
            SUBSCRIBE: 'SNAPSHOT_SUBSCRIBE_CANDLES',
            UNSUBSCRIBE: 'UNSUBSCRIBE_CANDLES'
        },
        messagePipe: pipe(
            filter((message: WebSocketMessage) => message.messageType in CandlesWebSocketMessageType),
            mergeMap((message: WebSocketMessage): Observable<Candle> => {
                const candles: Candle[] = this.readCandlesMessage(message);

                return of(...candles);
            })
        ),
        subscriptionSent$: this.subscriptionSentSubject,
        disconnected$: this.disconnectedSubject,
        subjectPipeFactory: subject => this.createSubjectPipe(subject),
        deprecateSubject: subject => this.deprecateSubject(subject)
    };

    private dataBank: {
        [dataBankId: string]: Candle[]
    } = {};

    constructor(
        private subscriptionStreamingService: SubscriptionStreamingService,

        // [HED-930] It's not SmartSecurityService here, since it requires accounts
        private securityService: SecurityService
    ) {
        this.subscriptionSent$ = this.subscriptionSentSubject.asObservable();
        this.disconnected$ = this.disconnectedSubject.asObservable();
    }

    public createSubscription(securityKeyStr: string, resolution: ResolutionString): Observable<Candle> {
        const securityKey = parseSecurityKey(securityKeyStr);
        const getSubject = (securityKeyToSubscribe: SecurityKey) =>
            [CandleStreamingService.createDataBankIdBySecurityKeyAndResolution(
                formatSecurityKey(securityKeyToSubscribe),
                resolution
            )];

        // [TT-247] Handle Smart security (workaround)
        return defer(() => {
            let stream$: Observable<Candle>;

            if (isSmartSecurityKey(securityKey)) {
                let security: SmartSecurity;

                stream$ = (this.securityService as SmartSecurityService).resolveSmartSecurity(securityKey).pipe(
                    map(securities => securities && securities[0]),
                    tap(() =>
                        security = this.securityService
                            .getSecurityFromCacheBySecurityKey(securityKey) as SmartSecurity
                    ),
                    switchMap(securityReplacement => securityReplacement
                        ? this.subscriptionStreamingService.createSubscription(
                            this.subscriptionSubjectType,
                            getSubject(securityReplacement)
                        ).pipe(
                            map<Candle, Candle>(candle => ({
                                ...candle,
                                security
                            }))
                        )
                        : NEVER
                    )
                );
            } else {
                stream$ = this.subscriptionStreamingService
                    .createSubscription(this.subscriptionSubjectType, getSubject(securityKey));
            }

            return stream$;
        });
    }

    public getCandlesFromCache(securityKeyStr: string, resolution: ResolutionString): Candle[] {
        const dataBankId = CandleStreamingService
            .createDataBankIdBySecurityKeyAndResolution(securityKeyStr, resolution);

        return this.dataBank[dataBankId];
    }

    private readCandlesMessage(message: CandlesSnapshotWsMessage | CandlesUpdateWsMessage): Candle[] {
        let candles: Candle[];

        switch (message.messageType) {

            case CandlesWebSocketMessageType.CANDLES_SNAPSHOT:
                candles = message.data.data;
                candles.sort((candle1, candle2) =>
                    moment(candle1.closeTime).valueOf() - moment(candle2.closeTime).valueOf()
                );
                this.updateCandlesCache(candles);
                break;

            case CandlesWebSocketMessageType.CANDLES_UPDATE:
                candles = message.data;
                this.cacheCandlesToDataBank(candles);
                break;

            default:
                candles = [];
                break;
        }

        return candles;
    }

    private cacheCandlesToDataBank(candles: Candle[]): void {
        const addCandleToBank = (candle: Candle) => {
            const dataBankId = CandleStreamingService.createDataBankIdByCandle(candle);
            const dataBankElement = this.dataBank[dataBankId];

            if (dataBankElement) {

                if (dataBankElement.length > CANDLES_LIMIT) {
                    dataBankElement.shift();
                }

                const insertionIndex = dataBankElement.findIndex(
                    currentCandle => currentCandle.closeTime === candle.closeTime
                );

                if (insertionIndex !== -1) {
                    dataBankElement[insertionIndex] = candle;
                } else {
                    dataBankElement.push(candle);
                }

            } else {
                this.dataBank[dataBankId] = [candle];
            }
        };

        candles.forEach(addCandleToBank);
    }

    private updateCandlesCache(candles: Candle[]): void {
        candles.forEach(candle => delete this.dataBank[CandleStreamingService.createDataBankIdByCandle(candle)]);

        this.cacheCandlesToDataBank(candles);
    }

    private createSubjectPipe(subject: SubscriptionSubject): OperatorFunction<Candle, Candle> {
        const knownCandles$: Observable<Candle> = defer(() => {
            const knownCandles = subject
                .map(dataBankId => this.dataBank[dataBankId])
                .filter(currentCandles => !!currentCandles)
                .reduce((allCandles, currentCandles) => allCandles.concat(currentCandles), []);

            return of(...knownCandles);
        });

        return pipe(
            filter((candle: Candle) => subject.includes(CandleStreamingService.createDataBankIdByCandle(candle))),
            source => merge(knownCandles$, source)
        );
    }

    private deprecateSubject(subject: SubscriptionSubject): void {
        subject.forEach(dataBankId => delete this.dataBank[dataBankId]);
    }

}
